<template>
    <div class="campaignsPopup w-full relative">
        <div v-if="loaded" class="text-center">
            <c-loader color="rgba(var(--vs-primary),1)"></c-loader>
        </div>
        <ValidationObserver v-else ref="form" v-slot="{handleSubmit}">
            <vs-row>
                <vs-col :vs-w="isGroupStatus ? 6 : 12">
                    <ValidationProvider vid="name" :name="$t('modals.Campaign_name')" rules="required|minmax:3,50" v-slot="{ errors }">
                        <div class="p-2">
                            <vs-input
                                class="w-full"
                                name="name"
                                :label="$t('modals.Campaign_name')"
                                :placeholder="$t('modals.Enter_campaign_name')"
                                v-model="name"
                            />
                            <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                        </div>
                    </ValidationProvider>
                </vs-col>

                <vs-col vs-w="6" v-if="isGroupStatus">
                    <ValidationProvider vid="team" :name="$t('global.Teams')" rules="required" v-slot="{ errors }">
                        <div class="p-2">
                            <div class="vs-input--label mb-1">{{ $t('global.Teams') }}</div>
                            <v-select
                                class="w-full popup__select"
                                v-model="team"
                                :reduce="item => item.id.toString()"
                                label="name"
                                appendToBody
                                autocomplete
                                :options="options.teams"
                                :placeholder="$t('modals.Choose_team')"
                                :disabled="popupType === 'editCampaign' && userState.group < 2"
                                :calculate-position="withPopper"
                            />
                            <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                        </div>
                    </ValidationProvider>
                </vs-col>

                <vs-col vs-w="6">
                    <ValidationProvider vid="offer" :name="$t('global.Offer')" rules="required" v-slot="{ errors }">
                        <div class="p-2">
                            <div class="vs-input--label mb-1">{{ $t('global.Offer') }}</div>
                            <v-select
                                class="w-full popup__select"
                                :disabled="!team || popupType === 'editCampaign' && userState.group < 2"
                                v-model="offer"
                                :reduce="item => item.id.toString()"
                                label="name"
                                appendToBody
                                autocomplete
                                :options="options.offers"
                                :placeholder="$t('modals.Choose_offer')"
                                :calculate-position="withPopper"
                            />
                            <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                        </div>
                    </ValidationProvider>
                </vs-col>

                <vs-col vs-w="6">
                    <ValidationProvider vid="language" :name="$t('global.Language')" rules="required" v-slot="{ errors }">
                        <div class="p-2">
                            <div class="vs-input--label mb-1">{{ $t('global.Language') }}</div>
                            <v-select
                                class="w-full popup__select"
                                :disabled="!offer || popupType === 'editCampaign' && userState.group < 2"
                                v-model="language"
                                appendToBody
                                autocomplete
                                :options="options.languages"
                                :placeholder="$t('modals.Choose_language')"
                                :calculate-position="withPopper"
                            />
                            <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                        </div>
                    </ValidationProvider>
                </vs-col>

                <vs-col vs-w="6">
                    <div class="flex w-full items-end">
                        <div class="p-2 w-full">
                            <ValidationProvider vid="landing" :name="$t('global.Landing')" rules="required" v-slot="{ errors }" class="w-full">
                                <div class="vs-input--label mb-1">{{ $t('global.Landing') }}</div>
                                <v-select
                                    class="w-full popup__select"
                                    :disabled="!language && !offer || popupType === 'editCampaign' && userState.group < 2"
                                    v-model="landing"
                                    :reduce="item => item.id.toString()"
                                    label="name"
                                    appendToBody
                                    autocomplete
                                    :options="options.landings"
                                    :placeholder="$t('modals.Choose_landing')"
                                    :calculate-position="withPopper"
                                />
                                <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                            </ValidationProvider>
                        </div>

                        <div class="flex items-center mb-2" v-if="selectedLanding && Object.keys(selectedLanding).length">
                            <a
                                target="_blank"
                                class="p-2"
                                v-tooltip="selectedLanding.name"
                                :href="selectedLanding.preview_url"
                            >
                                <vs-icon v-bind="$iconData('icon-eye', 'fi-rr-eye')"></vs-icon>
                            </a>
                            <a
                                class="p-2"
                                v-tooltip="'Copy url'"
                                href="#"
                                @click.prevent="copyLink(selectedLanding.preview_url)"
                            >
                                <vs-icon v-bind="$iconData('icon-copy', 'fi-rr-copy-alt')"></vs-icon>
                            </a>
                        </div>
                    </div>
                </vs-col>

                <vs-col vs-w="6">
                    <ValidationProvider vid="affiliate" :name="$t('global.Affiliate')" rules="required" v-slot="{ errors }">
                        <div class="p-2">
                            <div class="vs-input--label mb-1">{{ $t('global.Affiliate') }}</div>
                            <v-select
                                class="w-full popup__select"
                                :disabled="!team || popupType === 'editCampaign' && userState.group < 2"
                                v-model="affiliate"
                                :reduce="item => item.hash"
                                label="name"
                                appendToBody
                                autocomplete
                                :options="options.affiliates"
                                :placeholder="$t('modals.Choose_affiliate')"
                                :calculate-position="withPopper"
                            />
                            <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                        </div>
                    </ValidationProvider>
                </vs-col>

                <vs-col vs-w="12" v-if="!campaign_type">
                    <ValidationProvider vid="domain" :name="$t('global.Domain')" rules="required" v-slot="{ errors }">
                        <div class="p-2">
                            <div class="vs-input--label mb-1">{{ $t('global.Domain') }}</div>
                            <v-select
                                class="w-full popup__select"
                                v-model="domain"
                                :disabled="!affiliate || popupType === 'editCampaign' && userState.group < 2"
                                :reduce="item => item.id"
                                label="name"
                                multiple
                                :closeOnSelect="false"
                                autocomplete
                                :options="options.domains"
                                :placeholder="$t('modals.Choose_domain')"
                            />
                            <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                        </div>
                    </ValidationProvider>
                </vs-col>

                <vs-col vs-w="6" v-if="domain && domain.length && !campaign_type">
                    <div class="p-2 flex">
                        <vs-checkbox :disabled="popupType === 'editCampaign' && userState.group < 2" class="m-0 whitespace-no-wrap" v-model="landingPath">{{ $t('campaigns.Landing_path') }}</vs-checkbox>
                        <div class="ml-4">
                            <vs-input class="w-full" :disabled="!landingPath || popupType === 'editCampaign' && userState.group < 2" :placeholder="$t('label.new_path')" v-model="landing_slug" />
                        </div>
                    </div>
                </vs-col>

                <vs-col vs-w="6" v-if="domain && domain.length && !campaign_type">
                    <div class="p-2">
                        <div v-for="(item, index) of domainsList" :key="index" class="flex items-center mb-1">
                            <div>
                                <vs-button :disabled="popupType === 'editCampaign' && userState.group < 2" radius color="primary" v-tooltip="$t('tooltip.Delete')" @click="removeDomain(index)" type="flat" v-bind="$iconData('icon-x', 'fi-rr-cross-small')"></vs-button>
                            </div>
                            <div>
                                <vs-button :disabled="popupType === 'editCampaign' && userState.group < 2" radius color="primary" v-tooltip="$t('tooltip.Show')" target :href="`https://${item.name}/`" type="flat" v-bind="$iconData('icon-eye', 'fi-rr-eye')"></vs-button>
                            </div>
                            <div>
                                <vs-input :disabled="popupType === 'editCampaign' && userState.group < 2" class="w-full ml-1" disabled type="text" :value="item.name" />
                            </div>
                        </div>
                    </div>
                </vs-col>

                <vs-col vs-w="6">
                    <div class="p-2 mt-8">
                        <vs-checkbox :disabled="popupType === 'editCampaign' && userState.group < 2" class="m-0 whitespace-no-wrap" v-model="isPostback">{{ $t('sideMenu.Postbacks') }}</vs-checkbox>
                    </div>
                </vs-col>

                <vs-col vs-w="6">
                    <div class="p-2">
                        <ValidationProvider vid="postbacks" :name="$t('sideMenu.Postbacks')" :rules="{required: isPostback}" v-slot="{ errors }">
                            <div class="vs-input--label mb-1">{{ $t('sideMenu.Postbacks') }}</div>
                            <v-select
                                class="w-full popup__select"
                                v-model="postback"
                                :disabled="!isPostback || popupType === 'editCampaign' && userState.group < 2"
                                :reduce="item => item.id"
                                label="name"
                                appendToBody
                                multiple
                                :closeOnSelect="false"
                                autocomplete
                                :options="options.tracker"
                                :placeholder="$t('modals.Choose_postback')"
                                :calculate-position="withPopper"
                            />
                            <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                        </ValidationProvider>
                    </div>
                </vs-col>

                <vs-col vs-w="6" v-if="!campaign_type">
                    <div class="p-2 mt-8">
                        <vs-checkbox :disabled="popupType === 'editCampaign' && userState.group < 2" class="m-0 whitespace-no-wrap" v-model="isCloaking">{{ $t('campaigns.Cloaking') }}</vs-checkbox>
                    </div>
                </vs-col>

                <vs-col vs-w="6" v-if="!campaign_type">
                    <div class="p-2">
                        <ValidationProvider vid="cloaking" :name="$t('campaigns.Cloaking')" :rules="{required: isCloaking}" v-slot="{ errors }">
                            <div class="vs-input--label mb-1">{{ $t('campaigns.Cloaking') }}</div>
                            <v-select
                                class="w-full popup__select"
                                v-model="cloaking"
                                :disabled="!isCloaking || popupType === 'editCampaign' && userState.group < 2"
                                appendToBody
                                autocomplete
                                :options="options.cloaking"
                                :placeholder="$t('modals.Choose_cloaking')"
                                :calculate-position="withPopper"
                            />
                            <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                        </ValidationProvider>
                    </div>
                </vs-col>

                <vs-col vs-w="12" v-if="popupType === 'editCampaign'">
                    <div class="p-2">
                        <ValidationProvider vid="campaign_identifier" :name="$t('modals.Campaign_identifier')" rules="required" v-slot="{ errors }">
                            <vs-input
                                class="w-full"
                                name="name"
                                :label="$t('modals.Campaign_identifier')"
                                :placeholder="$t('modals.Enter_campaign_identifier')"
                                v-model="campaign_identifier"
                                :disabled="popupType === 'editCampaign' && userState.group < 2"
                            />
                            <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                        </ValidationProvider>
                    </div>
                </vs-col>

                <vs-col vs-w="12" v-if="platform">
                    <div class="flex items-end justify-between" v-for="(parameter, index) of parametersList" :key="index">
                        <div class="p-2 w-full">
                            <vs-input :disabled="popupType === 'editCampaign' && userState.group < 2" class="w-full" label="Name" v-model="parameter.name"/>
                        </div>
                        <div class="p-2 w-full">
                            <vs-input :disabled="popupType === 'editCampaign' && userState.group < 2" class="w-full" label="Value" v-model="parameter.value"/>
                        </div>
                        <div class="p-2">
                            <vs-button v-if="parametersList && index === parametersList.length - 1" radius color="primary" @click="addParameter" type="flat" v-bind="$iconData('icon-plus', 'fi-rr-plus')"></vs-button>
                            <vs-button v-else radius color="primary" @click="removeParameter(index)" type="flat" v-bind="$iconData('icon-minus', 'fi-rr-minus-small')"></vs-button>
                        </div>
                    </div>
                </vs-col>

                <vs-col vs-w="12">
                    <div class="p-2 text-right">
                        <vs-button @click="handleSubmit(submit)" color="primary" type="filled">{{ $t('buttons.Save') }}</vs-button>
                    </div>
                </vs-col>
            </vs-row>
        </ValidationObserver>
    </div>
</template>

<script>
import vSelect from 'vue-select'
import {ValidationObserver, ValidationProvider} from 'vee-validate'
import send from '@/utils/fetch'
import {copy} from '@/utils/utils'
import ErrorHandler from "@/utils/errorHandler"
import EventBus from "@/store/eventBus"
import {vSelectPosition, utils} from '@/utils/mixins'

export default {
    mixins: [vSelectPosition, utils],

    props: {
        popupData: {
            type: Object,
            required: true
        },
        popupButton: {
            type: String
        },
        popupType: {
            type: String
        }
    },

    data() {
        return {
            loaded: true,
            landingPath: false,
            isPostback: false,
            isCloaking: false,

            campaign_type: true,
            name: '',
            team: '',
            offer: '',
            language: '',
            landing: '',
            affiliate: '',
            domain: [],
            domainsList: [],
            landing_slug: '',
            postback: [],
            cloaking: '',
            platform: '',
            campaign_identifier: '',

            options: {
                teams: [],
                offers: [],
                languages: [],
                landings: [],
                affiliates: [],
                domains: [],
                tracker: [],
                cloaking: ['HideClick', 'MagicChecker', 'Adscpect'],
                platform: [
                    {label: 'FaceBook', value: 'facebook'},
                    {label: 'Google', value: 'google'},
                    {label: 'Yandex', value: 'yandex'}
                ],
            },

            parametersList: [{name: '', value: ''}],

            landingsSettings: [],
            selectedLanding: {},
            selectDomains: []
        }
    },

    components: {
        vSelect,
        ValidationObserver,
        ValidationProvider
    },

    watch: {
        async team(team_id, old_teams_id) {
            if (!team_id || (old_teams_id && team_id !== old_teams_id)) {
                this.offer = ''
                this.affiliate = ''
            }

            const response = await this.getData(team_id)

            const list = ['teams', 'offers', 'tracker']
            list.map(key => this.options[key] = response[key])

            this.landingsSettings = response.landings

            const result = this.options.teams.find(item => item.id === +team_id)
            if (result) this.options.affiliates = result.users

            this._clearSelectedElement('offers', 'offer', +this.offer)
            this._clearSelectedElement('affiliates', 'affiliate', this.affiliate)
        },

        offer(offer_id) {
            if (this.landingsSettings.length) {
                const languages = this.landingsSettings.filter(item => item.offer_id === +offer_id).map(landing => landing.language)
                this.options.languages = [...new Set(languages)]

                this.options.landings = this.landingsSettings.filter(item => item.offer_id === +offer_id  && item.language === this.popupData.language)
                this._clearSelectedElement('languages', 'language', this.language)
                this._clearSelectedElement('landings', 'landing', +this.landing)
            }
        },

        language(language) {
            this.options.landings = this.landingsSettings.filter(item => item.offer_id === +this.offer && item.language === language)
            this._clearSelectedElement('landings', 'landing', +this.landing)
        },

        landing(landing_id) {
            const landing = this.options.landings.find(item => item.id === +landing_id)
            if (landing) {
                this.selectedLanding = landing
            } else {
                this.selectedLanding = {}
            }
        },

        async affiliate(hash) {
            try {
                if (hash) {
                    const params = {
                        affiliate_hash: hash
                    }

                    if (this.popupType === 'editCampaign' && !this.campaign_type) {
                        params.campaign_id = this.popupData.id
                    }

                    if (this.popupType === 'addCampaign' && !this.campaign_type) {
                        const response = await send(`${this.$url.campaigns["get-additional-data"]}`, 'GET', {params}).then(response => response.data)
                        this.options.domains = response.domains
                        this.options.tracker = response.tracker_callbacks
                    }

                    if (this.popupData.affiliate_hash === hash) {
                        this.domain = this.popupData.domains
                    } else {
                        this.isPostback = false
                        this.domain = []
                    }
                } else {
                    this.isPostback = false
                    this.domain = []
                }
            } catch (e) {
                new ErrorHandler(e).send
            }
        },

        async isPostback(value) {
            if (!value) {
                this.postback = []
            }
        },

        isCloaking(value) {
            if (!value) {
                this.cloaking = ''
            }
        },

        landingPath(value) {
            if (!value) {
                this.landing_slug = ''
                this.domainsList = this.createDomainsObj(this.domain)
            }
        },

        landing_slug() {
            this.domainsList = this.createDomainsObj(this.domain)
        },

        domain(value) {
            if (value && value.length) {
                this.domainsList = this.createDomainsObj(value)
            } else {
                this.domainsList = value
            }
        },

        platform(item) {
            if (!item) {
                this.parametersList = [{ name: '', value: '' }]
            }
        }
    },

    computed: {
        isGroupStatus() {
            return this.$store.getters.userState.group === 2
        },

        userState() {
            return this.$store.getters.userState
        }
    },

    methods: {
        _clearSelectedElement(option, select, id) {
            const element = this.options[option].find(item => (item.hash || item.id || item) === id)
            if (!element) this[select] = ''
        },

        _notification(title, text, status, icon) {
            this.$vs.notify({title: title, text: text, color: status, iconPack: 'feather', icon: icon})
        },

        createDomainsObj(data) {
            return data.map(item => {
                const name = this.options.domains.find(domain => domain.id === item).name
                if (this.landingPath) {
                    return {name: `${name}/${this.landing_slug}`}
                } else {
                    return {name}
                }
            })
        },

        addParameter() {
            this.parametersList.push({name: '', value: ''})
        },

        removeParameter(index) {
            this.parametersList.splice(index, 1)
        },

        removeDomain(index) {
            this.domain.splice(index, 1);
        },

        copyLink(text) {
            let result = {}

            try {
                copy(text)
                result.text = text
                result.status = 'success'
                result.icon = 'icon-check'
                result.title = this.$t('notification.success')
            } catch (e) {
                result.text = this.$t('notification.errorMessage')
                result.status = 'danger'
                result.icon = 'icon-alert-circle'
                result.title = this.$t('notification.error')
            }

            if (Object.keys(result).length) {
                this._notification(result.title, result.text, result.status, result.icon)
            }
        },

        async submit() {
            try {
                const data = {
                    name: this.name.trim(),
                    landing_id: this.landing,
                    hash: this.affiliate,
                    domains: this.domain,
                    campaign_type: this.campaign_type ? 'api' : 'standard',
                    campaign_identifier: this.campaign_identifier.trim(),
                    platform_type: this.platform,
                    platform_url: this.parametersList,
                    tracker_callback_ids: this.postback,
                    cloack_type: this.cloaking,
                    landing_slug: this.landing_slug
                }

                if (!this.landing_slug) {
                    data.landing_slug = null
                }

                if (!this.isPostback) {
                    data.tracker_callback_ids = null
                }

                if (!this.isCloaking) {
                    data.cloack_type = null
                }

                if (this.campaign_type) {
                    data.domains = []
                    data.landing_slug = null
                    data.cloack_type = null
                }

                if (!data.platform_type || data.platform_url.find(el => el.name === '' || el.value === '')) {
                    data.platform_type = null
                    data.platform_url = null
                }

                let params = {}

                if (this.popupType === 'addCampaign') {
                    params.url = '/campaigns/create'
                    params.method = 'POST'
                }

                if (this.popupType === 'editCampaign') {
                    data.id = this.popupData.id
                    params.url = '/campaigns/update'
                    params.method = 'PUT'
                }

                await send(params.url, params.method, {data})

                EventBus.$emit('emitUpdateCampaigns')
                EventBus.$emit('emitPopupEvent', {type: 'close'})
            } catch (e) {
                if (e.response && e.response.data && e.response.data.result) {
                    const response = e.response.data.result
                    this.$refs.form.setErrors(this.$errors(response))
                }
                new ErrorHandler(e).send
            }
        },

        async getData(data, action = 'team_id') {
            try {
                let params = {}
                if (data && action === 'team_id') {
                    params.team_id = +data
                }

                if (data && action === 'affiliate_hash') {
                    params = data
                }

                return await send(this.$url.campaigns["get-additional-data"], 'GET', {params})
                    .then(response => response.data)
                    .then(response => {
                        response.teams = this.createNameById(response.teams)
                        return response
                    })
            } catch (e) {
                new ErrorHandler(e).send
            }
        }
    },

    async mounted() {
        try {
            this.loaded = true
            if (this.popupType === 'addCampaign') {
                const response = await this.getData()

                if (this.isGroupStatus) {
                    this.options.teams = response.teams
                } else {
                    this.team = this.userState.team_id
                }

                this.landingsSettings = response.landings
            }

            if (this.popupType === 'editCampaign') {
                this.campaign_type = this.popupData.campaign_type !== "standard"

                const response = await Promise.all([
                    this.getData(this.popupData.team_id),
                    this.getData({
                        affiliate_hash: this.popupData.affiliate_hash,
                        campaign_id: this.popupData.id
                    }, 'affiliate_hash')
                ])

                if (response[0] && response[1]) {
                    this.landingsSettings = response[0].landings
                    this.options.teams = response[0].teams
                    this.options.offers = response[0].offers

                    this.options.domains = response[1].domains
                    this.options.tracker = response[1].tracker_callbacks

                    this.team = this.popupData.team_id.toString()

                    this.$nextTick(() => {
                        if (this.options.teams.length) {
                            const result = this.options.teams.find(item => item.id === +this.team)
                            if (result) this.options.affiliates = result.users
                        }
                    })

                    this.language = this.popupData.language
                    this.offer = this.popupData.offer_id.toString()
                    this.name = this.popupData.campaign_name
                    this.landing = this.popupData.landing_id.toString()
                    this.affiliate = this.popupData.affiliate_hash
                    this.campaign_identifier = this.popupData.campaign_identifier

                    if (this.popupData.landing_slug) {
                        this.landingPath = true
                        this.landing_slug = this.popupData.landing_slug
                    }

                    if (this.popupData.platform_type) {
                        this.platform = this.popupData.platform_type
                    }

                    if (this.popupData.tracker_callback_ids && this.popupData.tracker_callback_ids.length) {
                        this.isPostback = true
                        this.postback = this.popupData.tracker_callback_ids
                    }

                    if (this.popupData.cloack_type) {
                        this.isCloaking = true
                        this.cloaking = this.popupData.cloack_type
                    }

                    if (this.popupData.platform_type && this.popupData.platform_url) {
                        this.platform = this.popupData.platform_type
                        this.parametersList = this.popupData.platform_url
                    }
                }
            }
            this.loaded = false
            this.$store.commit('UPDATE_LOADER_STATE', false)
        } catch (e) {
            this.$store.commit('UPDATE_LOADER_STATE_SET_ERROR', true)
            new ErrorHandler(e).send
        }
    }
}
</script>

<style lang="scss">
.campaignsPopup {
    .vs-input-primary {
        .vs-input--label {
            color: rgba(0, 0, 0, .7) !important;
        }

        .vs-input--placeholder {
            transform: none !important;
        }

        .vs-input--input {
            &:focus {
                border: 1px solid rgba(0, 0, 0, 0.2) !important;
                box-shadow: none !important;
            }
        }
    }
}

.vs__dropdown-option--selected {
    display: none !important;
}
</style>
